import React from 'react';
import {
  Box,
  Typography,
  Popover,
  Chip,
} from '@mui/material';
import { TrendingUp, ErrorOutline, CheckCircle, PersonAddAlt } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { NOTIFICATION_TYPES } from './notificationData';

const typeIconMap = {
  [NOTIFICATION_TYPES.SUCCESS]: { icon: <TrendingUp sx={{ fontSize: 20 }} />, bg: '#E8F5E9', color: '#2E7D32' },
  [NOTIFICATION_TYPES.WARNING]: { icon: <ErrorOutline sx={{ fontSize: 20 }} />, bg: '#FFF8E1', color: '#E1AD01' },
  [NOTIFICATION_TYPES.INFO]: { icon: <CheckCircle sx={{ fontSize: 20 }} />, bg: '#E8F5E9', color: '#2E7D32' },
  [NOTIFICATION_TYPES.USER]: { icon: <PersonAddAlt sx={{ fontSize: 20 }} />, bg: '#EDE7F6', color: '#7E57C2' },
};

const NotificationDropdown = ({ anchorEl, open, onClose, notifications, onMarkAllRead }) => {
  const navigate = useNavigate();
  const unreadCount = notifications.filter((n) => !n.read).length;
  const displayNotifications = notifications.slice(0, 4);

  const handleViewAll = () => {
    onClose();
    navigate('/notifications');
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          sx: {
            width: 380,
            maxHeight: 520,
            borderRadius: '16px',
            backgroundColor: '#FFFFFF',
            boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.15)',
            mt: 1.5,
            overflow: 'hidden',
          },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          py: 2,
          borderBottom: '1px solid #F0F0F0',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 20, color: '#1A1A1A' }}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Chip
              label={`${unreadCount} new`}
              size="small"
              sx={{
                backgroundColor: '#E1AD01',
                color: '#FFFFFF',
                fontWeight: 600,
                fontSize: 11,
                height: 24,
                borderRadius: '12px',
              }}
            />
          )}
        </Box>
        <Typography
          onClick={onMarkAllRead}
          sx={{
            color: '#E1AD01',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          Mark all read
        </Typography>
      </Box>

      {/* Notification List */}
      <Box sx={{ maxHeight: 380, overflowY: 'auto' }}>
        {displayNotifications.map((notification) => {
          const typeInfo = typeIconMap[notification.type];
          return (
            <Box
              key={notification.id}
              sx={{
                display: 'flex',
                gap: 1.5,
                px: 2.5,
                py: 2,
                cursor: 'pointer',
                backgroundColor: notification.read ? 'transparent' : '#FFFDF5',
                '&:hover': { backgroundColor: '#F9F9F9' },
                borderBottom: '1px solid #F5F5F5',
              }}
            >
              {/* Icon */}
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  backgroundColor: typeInfo.bg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: typeInfo.color,
                  flexShrink: 0,
                  mt: 0.3,
                }}
              >
                {typeInfo.icon}
              </Box>

              {/* Content */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 14,
                    color: '#1A1A1A',
                    lineHeight: 1.3,
                  }}
                >
                  {notification.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: '#555555',
                    lineHeight: 1.5,
                    mt: 0.3,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {notification.description}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.5 }}>
                  {!notification.read && (
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        backgroundColor: '#E1AD01',
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <Typography sx={{ fontSize: 12, color: '#999999' }}>
                    {notification.time}
                  </Typography>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          textAlign: 'center',
          py: 1.5,
          borderTop: '1px solid #F0F0F0',
        }}
      >
        <Typography
          onClick={handleViewAll}
          sx={{
            color: '#E1AD01',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          View all notifications
        </Typography>
      </Box>
    </Popover>
  );
};

export default NotificationDropdown;
